import {
	DraftCarton,
	PickOrderLine,
	PickedQuantities,
	activeLines,
	byOldestFulfillmentOrder,
	pickedFor,
	unpackedFor,
} from './draft'
import { ProductIdentity, productIdentity } from './productIdentity'

/**
 * One fulfillment order's claim on a product the packer is holding.
 *
 * `unpacked` is what is still loose on the bench for this order; `inThisCarton`
 * is what has already gone into the carton being filled.
 */
export type PackItemLine = {
	line: PickOrderLine,
	unpacked: number,
	inThisCarton: number,
}

/**
 * A product as the packer handles it: one entry per product, however many
 * fulfillment orders are waiting on it.
 */
export type PackItem = {
	/** `Part.id` — a number, not an `ID`: this product comes from the REST schema. */
	productId: PickOrderLine['product']['id'],
	identity: ProductIdentity,
	/** Every line for this product, oldest fulfillment order first. */
	lines: PackItemLine[],
	/** Picked, and so in the packer's hands. */
	inHand: number,
	/** In a carton — any carton, not just the one being filled. */
	packed: number,
	/** Still loose on the bench. */
	remaining: number,
	/** In the carton being filled. */
	inThisCarton: number,
}

/**
 * What the Pack step works from.
 *
 * Pick order lines are one per fulfillment order line item, so two orders
 * wanting the same product arrive as two lines. A packer handling one bin of products
 * should not have to split them by order — that is arithmetic the app can do —
 * so the lines are grouped by product here, the same way the Pick step groups
 * them, and the split is resolved on the way back out.
 *
 * Products nothing was picked for are dropped: there is nothing to pack.
 */
export function packItems(
	lines: PickOrderLine[],
	picked: PickedQuantities,
	cartons: DraftCarton[],
	activeCartonKey: string | null,
): PackItem[] {
	const active = cartons.find( ( carton ) => carton.key === activeCartonKey )
	const byProduct = new Map<PickOrderLine['product']['id'], PackItemLine[]>()

	for ( const line of activeLines( lines ) ) {
		if ( pickedFor( picked, line.id ) <= 0 ) {
			continue
		}
		const entries = byProduct.get( line.product.id ) ?? []
		entries.push( {
			line,
			unpacked: unpackedFor( picked, cartons, line.id ),
			inThisCarton: active?.contents[ line.id ] ?? 0,
		} )
		byProduct.set( line.product.id, entries )
	}

	return [ ...byProduct.entries() ].map( ( [ productId, entries ] ) => {
		const ordered = [ ...entries ].sort( ( a, b ) => byOldestFulfillmentOrder( a.line, b.line ) )
		const inHand = ordered.reduce( ( sum, entry ) => sum + pickedFor( picked, entry.line.id ), 0 )
		const remaining = ordered.reduce( ( sum, entry ) => sum + entry.unpacked, 0 )
		return {
			productId,
			identity: productIdentity( ordered[ 0 ].line.product ),
			lines: ordered,
			inHand,
			packed: inHand - remaining,
			remaining,
			inThisCarton: ordered.reduce( ( sum, entry ) => sum + entry.inThisCarton, 0 ),
		}
	} )
}

/**
 * Put `units` of a product into the carton being filled, oldest fulfillment
 * order first.
 *
 * Each order is served in full before the next one is started, which is the
 * rule the Pick step already applied when it settled how a short count divides
 * between orders. Carrying it forward means the order filled on the floor is
 * the order boxed first, so a run that stops before the bench is clear leaves
 * the gap with the same order both times. The oldest order ships whole; the
 * newest one waits.
 *
 * Returns the carton's new absolute quantity for each line it touched.
 */
export function allocateIntoCarton( item: PackItem, units: number ): Record<string, number> {
	const contents: Record<string, number> = {}
	let left = Math.max( units, 0 )

	for ( const entry of item.lines ) {
		const take = Math.min( entry.unpacked, left )
		contents[ entry.line.id ] = entry.inThisCarton + take
		left -= take
	}

	return contents
}

/**
 * Take `units` back out of the carton being filled, newest fulfillment order
 * first — the mirror of how they went in, so adding and removing the same
 * amount leaves the allocation where it started.
 */
export function removeFromCarton( item: PackItem, units: number ): Record<string, number> {
	const contents: Record<string, number> = {}
	let left = Math.max( units, 0 )

	for ( const entry of [ ...item.lines ].reverse() ) {
		const give = Math.min( entry.inThisCarton, left )
		contents[ entry.line.id ] = entry.inThisCarton - give
		left -= give
	}

	return contents
}

/** How many of a product the carton being filled could hold at most. */
export function cartonCapacityFor( item: PackItem ): number {
	return item.inThisCarton + item.remaining
}

/**
 * Set how many of a product are in the carton being filled, whatever direction
 * that moves in. Quantities beyond what the packer is holding are clamped
 * rather than rejected.
 */
export function setCartonQuantity( item: PackItem, target: number ): Record<string, number> {
	const capped = Math.min( Math.max( target, 0 ), cartonCapacityFor( item ) )
	return capped >= item.inThisCarton
		? allocateIntoCarton( item, capped - item.inThisCarton )
		: removeFromCarton( item, item.inThisCarton - capped )
}

/**
 * Which orders a set of units belongs to, e.g. `FO-165 ×2 · FO-171 ×4`.
 *
 * The packer never chooses this, but they can see it — and on a carton row it
 * is the difference between a box that completes an order and one that does
 * not.
 */
export function fulfillmentOrderBreakdown(
	item: PackItem,
	quantityOf: ( entry: PackItemLine ) => number,
): string {
	return item.lines
		.filter( ( entry ) => quantityOf( entry ) > 0 )
		.map( ( entry ) => `FO-${entry.line.fulfillmentOrder.orderNumber} ×${quantityOf( entry )}` )
		.join( ' · ' )
}

/** Where the units still on the bench are headed. */
export function remainingBreakdown( item: PackItem ): string {
	return fulfillmentOrderBreakdown( item, ( entry ) => entry.unpacked )
}

/** What the carton being filled is fulfilling, for this product. */
export function cartonBreakdown( item: PackItem ): string {
	return fulfillmentOrderBreakdown( item, ( entry ) => entry.inThisCarton )
}
